// src/app/party/party.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Party, PartyLinkResolution } from './party.model';
import { Page } from "../models/page.model";
import { Company } from '../company/company.model';
import { Customer } from '../customer/customer.model';
import { PartyType } from './party-type.enum';

@Injectable({
    providedIn: 'root'
})
export class PartyService {

    private baseUrl = `${environment.restApiUrl}parties`;

    constructor(private http: HttpClient) { }

    /**
     * Fetches a paginated list of parties from the backend.
     * @param owningCompanyId The mandatory ID of the company.
     * @param entityTypes An array of entity types to filter by (e.g., ['Company', 'Shipper']).
     * @param searchText Optional text to search across name, email, and phone.
     * @param page The page number to retrieve (0-indexed).
     * @param size The number of items per page.
     */
    getParties(
        owningCompanyId: number,
        entityTypes: string[] = [], // The array of types to filter by
        searchText: string = '',
        page: number = 0,
        size: number = 10
    ): Observable<Page<Party>> {
        let params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('page', page.toString())
            .set('size', size.toString());

        if (entityTypes.length) {
            params = params.set('partyTypes', entityTypes.join(','));
        }

        if (searchText) {
            params = params.append('searchText', searchText);
        }

        // Append each entity type to the parameters.
        // This allows your backend to receive a list like: &entityTypes=Company&entityTypes=Shipper
        entityTypes.forEach(type => {
            params = params.append('entityTypes', type);
        });

        return this.http.get<Page<Party>>(this.baseUrl, { params });
    }

    getPartyById(owningCompanyId: number, entityType: PartyType, entityId: number): Observable<Party> {
        return this.http.get<Party>(`${this.baseUrl}/${entityType}/${entityId}/${owningCompanyId}`);
    }

    linkPartyToCompany(linkedPartyId: number, owningCompanyId: number, partyType: string) {
        const url = `${this.baseUrl}/link-to-company`;
        const params = new HttpParams()
            .set('linkedPartyId', linkedPartyId.toString())
            .set('owningCompanyId', owningCompanyId.toString())
            .set('partyType', partyType);
        return this.http.post(url, params);
    }

    linkCompanyByCrNumber(owningCompanyId: number, crNumber: number | string): Observable<PartyLinkResolution> {
        const params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('crNumber', `${crNumber}`);
        return this.http.post<PartyLinkResolution>(`${this.baseUrl}/companies/link-by-cr`, null, { params });
    }

    linkContactByPhoneNumber(owningCompanyId: number, phoneNumber: string, partyType: PartyType): Observable<PartyLinkResolution> {
        const params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('phoneNumber', phoneNumber)
            .set('partyType', partyType);
        return this.http.post<PartyLinkResolution>(`${this.baseUrl}/contacts/link-by-phone`, null, { params });
    }

    // generate conversion function to convert party to all entityTypes: company & customer
    convertPartyToCompany(party: Party): Company {
        return {
            id: party.id.entityId,
            name: party.entityName,
            emailAddress: party.emailAddress,
            phoneNumber: party.phoneNumber,
            logo: party.imageUrl,
            crNumber: party.crNumber != null ? Number(party.crNumber) : undefined,
            taxRegNumber: party.taxRegNumber,
            address: party.address
        } as Company;
    }

    convertPartyToCustomer(party: Party): Customer {
        return {
            id: party.id.entityId,
            name: party.entityName,
            emailAddress: party.emailAddress,
            phoneNumber: party.phoneNumber,
            imageUrl: party.imageUrl,
            address: party.address,
        } as Customer;
    }

    convertToParty(object: any, partyType: PartyType, owningCompanyId: number): Party {
        if (!object) {
            return null as any;
        }

        const party: Partial<Party> = {
            owningCompanyId: owningCompanyId
        };

        if (partyType === PartyType.Company) {
            const company = object as Company;
            party.id = { entityId: company.id, entityType: PartyType.Company };
            party.entityName = company.name;
            party.emailAddress = company.emailAddress ?? undefined;
            party.phoneNumber = company.phoneNumber ?? undefined;
            party.imageUrl = company.logo ?? undefined;
            party.isCompany = true;
            party.address = company.address;
            party.crNumber = company.crNumber != null ? String(company.crNumber) : undefined;
            party.taxRegNumber = company.taxRegNumber ?? undefined;
        } else if (partyType === PartyType.TransportationAgency) {
            const agency = object as any; // Assuming TransportationAgency model
            party.id = { entityId: agency.rowId, entityType: PartyType.TransportationAgency };
            party.entityName = agency.agencyName;
            party.emailAddress = agency.emailAddress;
            party.phoneNumber = agency.phoneNumber;
            party.imageUrl = agency.logo;
            party.isCompany = true;
            party.address = agency.address;
            party.crNumber = agency.crNumber != null ? String(agency.crNumber) : undefined;
        } else if (partyType === PartyType.Customer) {
            const customer = object as Customer;
            party.id = { entityId: customer.id, entityType: PartyType.Customer };
            party.entityName = customer.name;
            party.emailAddress = customer.emailAddress;
            party.phoneNumber = customer.phoneNumber;
            party.imageUrl = customer.imageUrl;
            party.isCompany = false;
            party.address = customer.address;
        } else if (partyType === PartyType.Driver) {
            const driver = object as any; // Assuming Driver model
            party.id = { entityId: driver.id, entityType: PartyType.Driver };
            party.entityName = driver.name;
            party.emailAddress = driver.emailAddress;
            party.phoneNumber = driver.phoneNumber;
            party.imageUrl = driver.imageUrl;
            party.isCompany = false;
        } else if (partyType === PartyType.FreightForwarder) {
            const freightForwarder = object as any; // Assuming FreightForwarder model
            party.id = { entityId: freightForwarder.id, entityType: PartyType.FreightForwarder };
            party.entityName = freightForwarder.name;
            party.emailAddress = freightForwarder.emailAddress;
            party.phoneNumber = freightForwarder.phoneNumber;
            party.imageUrl = freightForwarder.logo;
            party.isCompany = true;
            party.address = freightForwarder.address;
            party.crNumber = freightForwarder.crNumber != null ? String(freightForwarder.crNumber) : undefined;
        } else {
            throw new Error(`Unsupported entity type or entity object: ${partyType}`);
        }
        return party as Party;
    }

}
